import type { SheetRow } from './googleSheetsApi';
import type { ProductDocType, ProductVariant } from '@lancar-pos/db-schema';
import type { CategoryDocType } from '@lancar-pos/db-schema';
import type { OrderDocType, OrderItem, OrderType } from '@lancar-pos/db-schema';
import type { CustomerDocType } from '@lancar-pos/db-schema';

export const PRODUCT_HEADERS = [
  'id', 'name', 'sku', 'barcode', 'price', 'cost', 'category_id',
  'unit', 'image_url', 'stock', 'min_stock', 'is_active',
  'variants', 'created_at', 'updated_at', '_deleted'
];

export const CATEGORY_HEADERS = [
  'id', 'name', 'color', 'icon', 'sort_order', 'parent_id',
  'created_at', 'updated_at', '_deleted'
];

export const ORDER_HEADERS = [
  'id', 'order_number', 'items', 'subtotal', 'discount', 'discount_type',
  'tax', 'total', 'payment_method', 'paid_amount', 'change_amount',
  'status', 'customer_id', 'table_number', 'order_type', 'kitchen_notes',
  'receipt_printed', 'synced_at', 'created_at', 'updated_at', '_deleted'
];

export const CUSTOMER_HEADERS = [
  'id', 'name', 'phone', 'email', 'address', 'notes',
  'total_spent', 'visit_count', 'created_at', 'updated_at', '_deleted'
];

export function productToRow(product: ProductDocType): SheetRow {
  return {
    id: product.id,
    name: product.name,
    sku: product.sku || '',
    barcode: product.barcode || '',
    price: String(product.price),
    cost: String(product.cost),
    category_id: product.categoryId || '',
    unit: product.unit,
    image_url: product.imageUrl || '',
    stock: String(product.stock),
    min_stock: String(product.minStock),
    is_active: product.isActive ? 'true' : 'false',
    variants: JSON.stringify(product.variants || []),
    created_at: product.createdAt,
    updated_at: product.updatedAt,
    _deleted: product._deleted ? 'true' : 'false',
  };
}

export function rowToProduct(row: SheetRow): ProductDocType {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku || '',
    barcode: row.barcode || '',
    price: parseFloat(row.price) || 0,
    cost: parseFloat(row.cost) || 0,
    categoryId: row.category_id || null,
    unit: row.unit || 'pcs',
    imageUrl: row.image_url || null,
    stock: parseFloat(row.stock) || 0,
    minStock: parseFloat(row.min_stock) || 0,
    isActive: row.is_active !== 'false',
    variants: parseJsonSafe<ProductVariant[]>(row.variants, []),
    bomItems: [],
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
    _deleted: row._deleted === 'true',
  };
}

export function categoryToRow(category: CategoryDocType): SheetRow {
  return {
    id: category.id,
    name: category.name,
    color: category.color || '',
    icon: category.icon || '',
    sort_order: String(category.sortOrder),
    parent_id: category.parentId || '',
    created_at: category.createdAt,
    updated_at: category.updatedAt,
    _deleted: category._deleted ? 'true' : 'false',
  };
}

export function rowToCategory(row: SheetRow): CategoryDocType {
  return {
    id: row.id,
    name: row.name,
    color: row.color || '#3B82F6',
    icon: row.icon || '',
    sortOrder: parseFloat(row.sort_order) || 0,
    parentId: row.parent_id || null,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
    _deleted: row._deleted === 'true',
  };
}

export function orderToRow(order: OrderDocType): SheetRow {
  return {
    id: order.id,
    order_number: order.orderNumber,
    items: JSON.stringify(order.items || []),
    subtotal: String(order.subtotal),
    discount: String(order.discount),
    discount_type: order.discountType,
    tax: String(order.tax),
    total: String(order.total),
    payment_method: order.paymentMethod,
    paid_amount: String(order.paidAmount),
    change_amount: String(order.changeAmount),
    status: order.status,
    customer_id: order.customerId || '',
    table_number: order.tableNumber || '',
    order_type: order.orderType,
    kitchen_notes: order.kitchenNotes || '',
    receipt_printed: order.receiptPrinted ? 'true' : 'false',
    synced_at: order.syncedAt || '',
    created_at: order.createdAt,
    updated_at: order.updatedAt,
    _deleted: order._deleted ? 'true' : 'false',
  };
}

export function rowToOrder(row: SheetRow): OrderDocType {
  return {
    id: row.id,
    orderNumber: row.order_number,
    items: parseJsonSafe<OrderItem[]>(row.items, []),
    subtotal: parseFloat(row.subtotal) || 0,
    discount: parseFloat(row.discount) || 0,
    discountType: (row.discount_type as 'percent' | 'amount') || 'amount',
    tax: parseFloat(row.tax) || 0,
    total: parseFloat(row.total) || 0,
    paymentMethod: row.payment_method as 'cash' | 'card' | 'qris' | 'mixed' || 'cash',
    paidAmount: parseFloat(row.paid_amount) || 0,
    changeAmount: parseFloat(row.change_amount) || 0,
    status: row.status as 'draft' | 'pending' | 'completed' | 'cancelled' | 'refunded' || 'draft',
    customerId: row.customer_id || null,
    tableNumber: row.table_number || null,
    orderType: row.order_type as OrderType || 'takeaway',
    kitchenNotes: row.kitchen_notes || '',
    receiptPrinted: row.receipt_printed === 'true',
    syncedAt: row.synced_at || null,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
    _deleted: row._deleted === 'true',
  };
}

export function customerToRow(customer: CustomerDocType): SheetRow {
  return {
    id: customer.id,
    name: customer.name,
    phone: customer.phone || '',
    email: customer.email || '',
    address: customer.address || '',
    notes: customer.notes || '',
    total_spent: String(customer.totalSpent),
    visit_count: String(customer.visitCount),
    created_at: customer.createdAt,
    updated_at: customer.updatedAt,
    _deleted: customer._deleted ? 'true' : 'false',
  };
}

export function rowToCustomer(row: SheetRow): CustomerDocType {
  return {
    id: row.id,
    name: row.name,
    phone: row.phone || '',
    email: row.email || null,
    address: row.address || null,
    notes: row.notes || null,
    totalSpent: parseFloat(row.total_spent) || 0,
    visitCount: parseInt(row.visit_count, 10) || 0,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
    _deleted: row._deleted === 'true',
  };
}

function parseJsonSafe<T>(str: string, defaultValue: T): T {
  if (!str || str.trim() === '') return defaultValue;
  try {
    return JSON.parse(str) as T;
  } catch {
    return defaultValue;
  }
}
